import React, { useContext, useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { useNavigate, useParams } from "react-router-dom";
import {
  Button,
  Flex,
  FormControl,
  FormErrorMessage,
  FormLabel,
  Grid,
  Input, Select,
  Switch,
} from "@chakra-ui/react";
import Card from "components/Card/Card";
import CardBody from "components/Card/CardBody";
import Loading from "components/Loading/Loading";
import ListPageCardHeader from "components/Card/ListPageCardHeader";
import { useCustomToast } from "components/CustomToast/CustomToast";
import { SidebarContext } from "contexts/SidebarContext";
import { checkErrors, hasError } from "utils/forms-utils";
import { equipmentsService, EquipmentType } from "services/equipmentsService";
import { ROUTES } from "constants/routes-path";
import moment from "moment";
import "moment/locale/pt-br";
import "react-datetime/css/react-datetime.css";
import DateTime from "react-datetime";
import { DATE_FORMAT, DATE_FORMAT_ISO, LOCALE_PT_BR } from "constants/date-time";

const EquipmentNew = () => {
  const { t } = useTranslation();
  const { id } = useParams();
  const { updateBreadcrumbKeyParams } = useContext(SidebarContext);
  const [formData, setFormData] = useState(undefined);
  const [equipmentSettingsData, setEquipmentSettingsData] = useState(undefined);
  const [inputError, setInputError] = useState({});
  const showToast = useCustomToast();
  const navigate = useNavigate();

  const ids = useMemo(() => ({
    id: "equipment-id",
    address: "equipment-address",
    active: "equipment-active",
    brand: "equipment-brand",
    model: "equipment-model",
    serialNumber: "equipment-serial-number",
    type: "equipment-type",
    certificate: "equipment-certificate",
    verificationDate: "equipment-verification-date",
    initialAitNumber: "ait-initial-number",
    finalAitNumber: "ait-final-number",
    imei: "equipment-imei",
  }), []);

  const isEdit = !!id;

  const isImeiMobileRequired = equipmentSettingsData
    ? equipmentSettingsData.imei_mobile_required === "true"
    : false;

  const isMobile = formData
    ? formData[ids.type]?.value === EquipmentType.MOBILE.value
    : false;

  const isRadar = formData
    ? formData[ids.type]?.value === EquipmentType.RADAR.value
    : false;

  const isFormInvalid = formData && inputError
    ? hasError({ formData, inputError })
    : true;

  const typesList = useMemo(() => [
    EquipmentType.RADAR,
    EquipmentType.MOBILE,
  ], []);

  const initFormData = (data = {}, { isMobile = false, isRadar = false, isImeiMobileRequired = false } = {}) => ({
    [ids.id]: { value: data?.id ?? "" },
    [ids.address]: { value: data?.endereco ?? "" },
    [ids.active]: { value: data?.ativo ?? false },
    [ids.brand]: {
      value: data?.marca ?? "",
      validations: [{ required: isMobile }],
    },
    [ids.model]: {
      value: data?.modelo ?? "",
      validations: [{ required: isMobile }],
    },
    [ids.serialNumber]: {
      value: data?.numero_serie ?? "",
      validations: [{ required: !isEdit }],
    },
    [ids.type]: {
      value: data?.tipo ?? null,
      validations: [{ required: !isEdit }]},
    [ids.certificate]: { value: data?.certificado_afericao ?? "" },
    [ids.verificationDate]: { value: data?.data_verificacao ?? "" },
    [ids.initialAitNumber]: {
      value: data?.numero_ait_inicial ?? "",
      validations: [{ required: isMobile }],
    },
    [ids.finalAitNumber]: {
      value: data?.numero_ait_final ?? "",
      validations: [{ required: isMobile }],
    },
    [ids.imei]: {
      value: data?.imei ?? "",
      validations: [{ required: isMobile && isImeiMobileRequired }],
    },
  });

  const dynamicValidationsMap = {
    [ids.brand]: ({ isMobile }) => [{ required: isMobile }],
    [ids.model]: ({ isMobile }) => [{ required: isMobile }],
    [ids.serialNumber]: ({ isEdit }) => [{ required: !isEdit }],
    [ids.initialAitNumber]: ({ isMobile }) => [{ required: isMobile }],
    [ids.finalAitNumber]: ({ isMobile }) => [{ required: isMobile }],
    [ids.imei]: ({ isMobile, isImeiMobileRequired }) => [{ required: isMobile && isImeiMobileRequired }],
  };

  const updateValidations = (prevFormData, states) => {
    const updatedFormData = { ...prevFormData };

    Object.entries(dynamicValidationsMap).forEach(([fieldId, getValidations]) => {
      if (!updatedFormData[fieldId]) return;

      updatedFormData[fieldId] = {
        ...updatedFormData[fieldId],
        validations: getValidations(states),
      };
    });

    return updatedFormData;
  };

  const handleInputChange = (e) => {
    const { id, type } = e.target;
    const value = type === "checkbox" ? e.target.checked : e.target.value;

    let newFormData = { ...formData, [id]: { ...formData[id], value } };

    if (id === ids.type) {
      const selectedType = value;
      const isMobile = selectedType === EquipmentType.MOBILE.value;
      const isRadar = selectedType === EquipmentType.RADAR.value;

      const states = {
        isMobile,
        isRadar,
        isImeiMobileRequired
      };

      const updatedFormData = updateValidations(newFormData, states);
      setFormData(updatedFormData);

      const dependentFields = Object.keys(dynamicValidationsMap);
      const newInputError = { ...inputError };
      dependentFields.forEach(field => {
        if (newInputError[field]) {
          delete newInputError[field];
        }
      });

      const updatedInputError = checkErrors({ formData: updatedFormData, inputError: newInputError, name: id });
      setInputError(updatedInputError);
    } else {
      setFormData(newFormData);
      setInputError(checkErrors({ formData: newFormData, inputError, name: id }));
    }
  };

  const handleVerificationDateChange = (value) => {
    const dateValue = moment(value).isValid()
      ? moment(value).format(DATE_FORMAT_ISO)
      : "";

    const updated = {
      ...formData,
      [ids.verificationDate]: {
        ...formData[ids.verificationDate],
        value: dateValue,
      },
    };

    setFormData(updated);

    setInputError((prev) =>
      checkErrors({
        formData: updated,
        inputError: prev,
        name: ids.verificationDate,
      })
    );
  };

  const fetchSettings = async () => {
    try {
      const settings = await equipmentsService.settings();
      setEquipmentSettingsData(settings);
    } catch (error) {
      showToast({ title: t(error), status: "error" });
      redirectToList();
    }
  };

  const fetchData = async () => {
    try {
      const data = await equipmentsService.find(id);

      const isMobile = data.tipo === EquipmentType.MOBILE.value;
      const isRadar = data.tipo === EquipmentType.RADAR.value;
      const isImeiMobileRequired = equipmentSettingsData?.imei_mobile_required === "true";

      const initialFormData = initFormData(data, { isMobile, isRadar, isImeiMobileRequired });

      const states = {
        isMobile,
        isRadar,
        isImeiMobileRequired,
        isEdit,
      };

      const validatedFormData = updateValidations(initialFormData, states);

      setFormData(validatedFormData);
      updateBreadcrumbKeyParams({ serialNumber: data.numero_serie });
    } catch (error) {
      showToast({ title: t(error), status: "error" });
      redirectToList();
    }
  };

  const redirectToList = () => {
    navigate(ROUTES.equipmentsPath);
  };

  const toDTO = () => ({
    id: formData[ids.id].value,
    endereco: formData[ids.address].value.trim(),
    ativo: formData[ids.active].value,
    marca: formData[ids.brand].value.trim(),
    modelo: formData[ids.model].value.trim(),
    numero_serie: formData[ids.serialNumber].value.trim(),
    tipo: formData[ids.type].value.trim(),
    certificado_afericao: formData[ids.certificate].value.trim(),
    data_verificacao: formData[ids.verificationDate].value.trim(),
    numero_ait_inicial: formData[ids.initialAitNumber].value.trim(),
    numero_ait_final: formData[ids.finalAitNumber].value.trim(),
    imei: formData[ids.imei].value.trim(),
  });

  const handleSubmit = async () => {
    if (isFormInvalid) return;

    try {
      const data = toDTO();

      if (id) {
        await equipmentsService.update(id, data);
      } else {
        await equipmentsService.create(data);
      }

      showToast({ title: t(`settings.equipment.messages.success.${id ? "edit" : "create"}`), status: "success" });
      redirectToList();
    } catch (error) {
      showToast({ title: t(error), status: "error" });
    }
  };

  useEffect(() => {
    if (!formData || !equipmentSettingsData) return;

    const selectedType = formData[ids.type]?.value;
    const isMobile = selectedType === EquipmentType.MOBILE.value;
    const isRadar = selectedType === EquipmentType.RADAR.value;
    const isImeiMobileRequired = equipmentSettingsData.imei_mobile_required === "true";

    const states = {
      isMobile,
      isRadar,
      isImeiMobileRequired,
      isEdit,
    };

    const updatedFormData = updateValidations(formData, states);
    setFormData(updatedFormData);
  }, [equipmentSettingsData]);

  useEffect(() => {
    fetchSettings();
  }, []);

  useEffect(() => {
    if (!equipmentSettingsData) return;

    if (id) {
      fetchData();
    } else {
      const isImeiMobileRequired = equipmentSettingsData.imei_mobile_required === "true";
      const emptyForm = initFormData({}, { isMobile: false, isRadar: false, isImeiMobileRequired });
      setFormData(emptyForm);
    }
  }, [id, equipmentSettingsData]);

  return (
    <Flex direction="column" minH="89vh" align="center" pt={{ sm: "120px", md: "90px" }}>
      <Card>
        <ListPageCardHeader
          title={t(`settings.equipment.form.${id ? "edit" : "create"}.title`)}
          subtitle={t(`settings.equipment.form.${id ? "edit" : "create"}.description`)}
        />

        <CardBody px="22px">
          {formData ? (
            <Flex w="100%" gap="40px" direction="column">
              <Grid gap="30px" templateColumns={{ sm: "1fr", md: "repeat(2, 1fr)" }} templateRows={{ md: "repeat(2, 1fr)" }}>
                { !isEdit ? (
                  <FormControl isRequired>
                    <FormLabel fontSize="xs" fontWeight="bold">
                      {t("settings.equipment.form.fields.type.label")}
                    </FormLabel>
                    <Select
                      fontSize="sm"
                      id={ids.type}
                      value={formData[ids.type].value}
                      placeholder={t("settings.equipment.form.fields.type.placeholder")}
                      onChange={handleInputChange}
                    >
                      {typesList?.map((option, index) => (
                        <option key={index} value={option.value}>
                          {t(`settings.equipment.form.fields.type.options.${option.labelKey}`)}
                        </option>)
                      )}
                    </Select>
                  </FormControl>
                ) : null }

                { isMobile ? (
                  <FormControl display="flex" alignItems="center">
                    <FormLabel mb="0" fontSize="xs" fontWeight="bold">
                      {t("settings.equipment.form.fields.active.label")}
                    </FormLabel>
                    <Switch
                      id={ids.active}
                      isChecked={formData[ids.active].value}
                      onChange={handleInputChange}
                    />
                  </FormControl>
                ) : null }

                <FormControl isRequired={isMobile} isInvalid={inputError[ids.brand]}>
                  <FormLabel fontSize="xs" fontWeight="bold">
                    {t("settings.equipment.form.fields.brand.label")}
                  </FormLabel>
                  <Input
                    fontSize="sm"
                    variant="main"
                    maxLength={255}
                    id={ids.brand}
                    value={formData[ids.brand].value}
                    onChange={handleInputChange}
                    placeholder={t("settings.equipment.form.fields.brand.placeholder")}
                  />
                  <FormErrorMessage>
                    {t(inputError[ids.brand])}
                  </FormErrorMessage>
                </FormControl>

                <FormControl isRequired={isMobile} isInvalid={inputError[ids.model]}>
                  <FormLabel fontSize="xs" fontWeight="bold">
                    {t("settings.equipment.form.fields.model.label")}
                  </FormLabel>
                  <Input
                    fontSize="sm"
                    variant="main"
                    maxLength={255}
                    id={ids.model}
                    value={formData[ids.model].value}
                    onChange={handleInputChange}
                    placeholder={t("settings.equipment.form.fields.model.placeholder")}
                  />
                  <FormErrorMessage>
                    {t(inputError[ids.model])}
                  </FormErrorMessage>
                </FormControl>

                { !isEdit ? (
                  <FormControl isRequired={!isEdit} isInvalid={inputError[ids.serialNumber]}>
                    <FormLabel fontSize="xs" fontWeight="bold">
                      {t("settings.equipment.form.fields.serial-number.label")}
                    </FormLabel>
                    <Input
                      fontSize="sm"
                      variant="main"
                      maxLength={30}
                      id={ids.serialNumber}
                      value={formData[ids.serialNumber].value}
                      onChange={handleInputChange}
                      placeholder={t("settings.equipment.form.fields.serial-number.placeholder")}
                    />
                    <FormErrorMessage>
                      {t(inputError[ids.serialNumber])}
                    </FormErrorMessage>
                  </FormControl>
                ) : null }

                { isRadar ? (
                  <FormControl>
                    <FormLabel fontSize="xs" fontWeight="bold">
                      {t("settings.equipment.form.fields.address.label")}
                    </FormLabel>
                    <Input
                      fontSize="sm"
                      variant="main"
                      maxLength={250}
                      id={ids.address}
                      value={formData[ids.address].value}
                      onChange={handleInputChange}
                      placeholder={t("settings.equipment.form.fields.address.placeholder")}
                    />
                  </FormControl>
                ) : null }

                { isRadar ? (
                  <FormControl>
                    <FormLabel fontSize="xs" fontWeight="bold">
                      {t("settings.equipment.form.fields.certificate.label")}
                    </FormLabel>
                    <Input
                      fontSize="sm"
                      variant="main"
                      maxLength={255}
                      id={ids.certificate}
                      value={formData[ids.certificate].value}
                      onChange={handleInputChange}
                      placeholder={t("settings.equipment.form.fields.certificate.placeholder")}
                    />
                  </FormControl>
                ) : null }

                { isRadar ? (
                  <FormControl>
                    <FormLabel fontSize="xs" fontWeight="bold">
                      {t("settings.equipment.form.fields.verification-date.label")}
                    </FormLabel>
                    <DateTime
                      dateFormat={DATE_FORMAT}
                      timeFormat={false}
                      locale={LOCALE_PT_BR}
                      value={
                        formData[ids.verificationDate].value
                          ? moment(formData[ids.verificationDate].value)
                          : ""
                      }
                      renderInput={(props) => (
                        <Input
                          {...props}
                          fontSize="sm"
                          variant="main"
                          placeholder={t(
                            "settings.equipment.form.fields.verification-date.placeholder"
                          )}
                        />
                      )}
                      onChange={handleVerificationDateChange}
                    />
                  </FormControl>
                ) : null }

                { isMobile ? (
                  <FormControl isRequired={isMobile} isInvalid={inputError[ids.initialAitNumber]}>
                    <FormLabel fontSize="xs" fontWeight="bold">
                      {t("settings.equipment.form.fields.initial-ait-number.label")}
                    </FormLabel>
                    <Input
                      fontSize="sm"
                      variant="main"
                      maxLength={30}
                      id={ids.initialAitNumber}
                      value={formData[ids.initialAitNumber].value}
                      onChange={handleInputChange}
                      placeholder={t("settings.equipment.form.fields.initial-ait-number.placeholder")}
                    />
                    <FormErrorMessage>
                      {t(inputError[ids.initialAitNumber])}
                    </FormErrorMessage>
                  </FormControl>
                ) : null }

                { isMobile ? (
                  <FormControl isRequired={isMobile} isInvalid={inputError[ids.finalAitNumber]}>
                    <FormLabel fontSize="xs" fontWeight="bold">
                      {t("settings.equipment.form.fields.final-ait-number.label")}
                    </FormLabel>
                    <Input
                      fontSize="sm"
                      variant="main"
                      maxLength={30}
                      id={ids.finalAitNumber}
                      value={formData[ids.finalAitNumber].value}
                      onChange={handleInputChange}
                      placeholder={t("settings.equipment.form.fields.final-ait-number.placeholder")}
                    />
                    <FormErrorMessage>
                      {t(inputError[ids.finalAitNumber])}
                    </FormErrorMessage>
                  </FormControl>
                ) : null }

                { isMobile && isImeiMobileRequired ? (
                  <FormControl isRequired={isMobile && isImeiMobileRequired} isInvalid={inputError[ids.imei]}>
                    <FormLabel fontSize="xs" fontWeight="bold">
                      {t("settings.equipment.form.fields.imei.label")}
                    </FormLabel>
                    <Input
                      fontSize="sm"
                      variant="main"
                      maxLength={16}
                      id={ids.imei}
                      value={formData[ids.imei].value}
                      onChange={handleInputChange}
                      placeholder={t("settings.equipment.form.fields.imei.placeholder")}
                    />
                    <FormErrorMessage>
                      {t(inputError[ids.imei])}
                    </FormErrorMessage>
                  </FormControl>
                ) : null }
              </Grid>

              <Flex w="100%" justify="space-between">
                <Button
                  w="125px"
                  variant="no-effects"
                  bg="gray.100"
                  color="gray.700"
                  fontSize="sm"
                  fontWeight="bold"
                  onClick={redirectToList}
                >
                  {t("system.actions.cancel")}
                </Button>

                <Button
                  w="125px"
                  variant="dark"
                  fontSize="sm"
                  isDisabled={isFormInvalid}
                  onClick={handleSubmit}
                  _hover={isFormInvalid ? {} : undefined}
                >
                  {t("system.actions.save")}
                </Button>
              </Flex>
            </Flex>
          ) : (
            <Loading h="300px" w="100%" />
          )}
        </CardBody>
      </Card>
    </Flex>
  );
}

export default EquipmentNew;
